import React, { useMemo } from "react";
import { useSearch } from "../../../contexts/SearchContext";
import { ExtrasStepCardBody } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/ExtrasStepCard";
import { mapRawLdsSupplementsToMockupExtras } from "../../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { getAvailableExtras } from "../../unifiedFlightSearch/duffelExtras";
import { formatFlightReviewDisplayMoney } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveFlightReviewDisplayMoney";
import {
  buildLiveBaggageBreakdown,
  buildLiveExtrasCatalog,
  flightLegsForExtras,
  toggleLiveBaggageSelection,
  withLiveBaggageBreakdown,
} from "../bridge/liveStageModels";
import { useLiveFlightStage } from "../bridge/useLiveFlightStage";
import ExtrasScreen from "./ExtrasScreen";

/**
 * Thin live host. ExtrasScreen is the preview component.
 * LDS unit-fare sync stays in ExtrasStepCardBody (headless).
 * Selections write the existing extras object; pricing recomputes there.
 */
export default function ExtrasLive() {
  const { booking, search, presentation, onBack } = useLiveFlightStage();
  const { savedDeal } = useSearch();
  const { selectedFlightOffer, extras, updateExtras, passengers, nextStep, pricing } = booking;
  const rawDeal = selectedFlightOffer?.rawDeal ?? savedDeal ?? null;

  const catalog = useMemo(() => {
    const ldsRows = rawDeal ? mapRawLdsSupplementsToMockupExtras(rawDeal) : [];
    return buildLiveExtrasCatalog({
      roomInfo: search.roomInfo,
      passengers,
      ldsRows,
      duffelExtras: getAvailableExtras(selectedFlightOffer),
      extras,
      baggageLines: presentation.offer?.baggage?.summaryLines || [],
      fare: presentation.offer?.fare || null,
      flight: flightLegsForExtras(presentation.offer, presentation.search),
      pricingFxRate: presentation.priceVm?.pricingFxRate ?? null,
    });
  }, [
    rawDeal,
    search.roomInfo,
    passengers,
    selectedFlightOffer,
    extras,
    presentation.offer,
    presentation.search,
    presentation.priceVm?.pricingFxRate,
  ]);

  const duffelExtras = useMemo(
    () => getAvailableExtras(selectedFlightOffer),
    [selectedFlightOffer]
  );

  const summaryOffer = useMemo(() => {
    const total = Number(pricing?.extras);
    const extrasTotalLabel =
      Number.isFinite(total) && total > 0
        ? formatFlightReviewDisplayMoney(total, pricing?.currency || "ILS")
        : null;
    const breakdown = buildLiveBaggageBreakdown(catalog, extrasTotalLabel);
    return withLiveBaggageBreakdown(presentation.offer, breakdown);
  }, [catalog, presentation.offer, pricing?.extras, pricing?.currency]);

  const selectBaggage = (paxId, dirId, optionId) => {
    updateExtras((prev) =>
      toggleLiveBaggageSelection(prev, catalog, {
        passengerId: paxId,
        directionId: dirId,
        optionId,
        selected: true,
        duffelExtras,
      })
    );
  };

  const removeBaggage = (paxId, dirId, optionId) => {
    const slot = catalog.selections?.[paxId]?.[dirId];
    const resolvedId = optionId || (typeof slot === "string" ? slot : null);
    updateExtras((prev) =>
      toggleLiveBaggageSelection(prev, catalog, {
        passengerId: paxId,
        directionId: dirId,
        optionId: resolvedId,
        selected: false,
        duffelExtras,
      })
    );
  };

  return (
    <>
      <ExtrasStepCardBody
        headless
        hidePassengersEmbedded
        rawDeal={rawDeal}
        savedDealFallback={savedDeal}
        extras={extras}
        updateExtras={updateExtras}
        passengers={passengers}
      />
      <ExtrasScreen
        offer={summaryOffer}
        search={presentation.search}
        catalog={catalog}
        selections={catalog.selections}
        otherSelections={{}}
        onSelectBaggage={selectBaggage}
        onRemoveBaggage={removeBaggage}
        onToggleOther={(paxId, optionId) => selectBaggage(paxId, "whole", optionId)}
        onContinue={() => nextStep()}
        onBack={onBack}
      />
    </>
  );
}
